import React, { useEffect, useRef, useState } from 'react';
import { X, Camera, Image as ImageIcon, RotateCcw, Check, PenLine } from 'lucide-react';
import { sketchToWalls, fileToImageData } from './sketchScan.jsx';
import { computeSpaces } from './spaces.jsx';
import { arcPoints, isArc } from './arcWalls.jsx';
import { guessEntrance, pruneOutsideStubs } from './entrance.jsx';
import { floatingTextStubs } from './autoDividers.jsx';
import './sketchImport.scss';

// «از روی کاغذ»: راهنما → عکس (دوربین یا گالری) → دیدنِ دیوارهای پیدا‌شده روی همان عکس → «استفاده از این پلان».
// خودِ عکس فقط برای همین پیش‌نمایش است و به پلان نمی‌رود — فقط دیوارها و جای «ورودی».
// «ورودی» (یک نقطه، نه در): نوشته‌ای که بیرونِ خانه کنارِ یک دیوار است (sketchScan/entrance)؛ لمس روی عکس = جابه‌جایی‌اش.
// onApply({ walls (پیکسل)، entrance: {x, y} پیکسل یا null، areaM2 })
// propertyArea: متراژِ آگهی (مقیاسِ پلان از همین می‌آید)؛ اگر نبود، کاربر وارد می‌کند

const fa = (n, d = 0) => Number(n).toLocaleString('fa-IR', { maximumFractionDigits: d });
const toLatin = (s) => String(s || '').replace(/[۰-۹]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'.indexOf(d)).replace(/[^\d.]/g, '');

function SketchImport({ propertyArea, hasPlan, onApply, onClose }) {
  const [stage, setStage] = useState('intro'); // intro | working | preview | error
  const [result, setResult] = useState(null); // { walls, image, rooms, minSide }
  const [entrance, setEntrance] = useState(null); // { x, y } پیکسل
  const [error, setError] = useState('');
  const [area, setArea] = useState(propertyArea ? String(propertyArea) : '');
  const [lastFile, setLastFile] = useState(null);
  const [sensitive, setSensitive] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const canvasRef = useRef(null);

  const run = async (file, sens) => {
    if (!file) return;
    setLastFile(file);
    setStage('working');
    setError('');
    // یک فریم فرصت تا «در حالِ پردازش» دیده شود (پردازش روی همین گوشی است)
    await new Promise((r) => setTimeout(r, 60));
    try {
      const src = await fileToImageData(file);
      const r = sketchToWalls(src, { sensitivity: sens ? 1.7 : 1 });
      const spacesOf = (ws) => computeSpaces(ws.map((w, i) => ({ id: i + 1, ...w })), [], [], []);
      const minSide = Math.min(r.image.width, r.image.height);
      // کلمه‌ای که خطِ کرسی‌اش «دیوارِ» معلقِ کوتاه شده بود → دوباره نوشته
      const ft = floatingTextStubs(r.walls, minSide);
      // نوشته‌ای که به دیوارِ بیرونی چسبیده بود و یک دیوارِ کوتاهِ اضافه ساخته بود → دوباره نوشته
      const pruned = pruneOutsideStubs(ft.walls, spacesOf(ft.walls), minSide);
      const walls = pruned.walls;
      const rooms = spacesOf(walls);
      const labels = [...(r.labels || []), ...ft.labels];
      setResult({ ...r, walls, rooms, minSide });
      const g = guessEntrance(walls, rooms, [...labels, ...pruned.stubs], minSide);
      setEntrance(g);
      setStage('preview');
    } catch (e) {
      console.error('[sketch]', e); // eslint-disable-line no-console
      setError('خواندنِ عکس ممکن نشد؛ دوباره امتحان کنید.');
      setStage('error');
    }
  };

  // لمس روی پیش‌نمایش = «ورودی این‌جاست»
  const pickEntrance = (e) => {
    const cv = canvasRef.current;
    if (!cv || !result) return;
    const b = cv.getBoundingClientRect();
    setEntrance({ x: ((e.clientX - b.left) / b.width) * cv.width, y: ((e.clientY - b.top) / b.height) * cv.height });
  };

  // پیش‌نمایش: عکسِ صاف‌شده + دیوارهای پیدا‌شده (قرمز) + فضاهای بسته (آبیِ کم‌رنگ) + ورودی (سبز)
  useEffect(() => {
    if (stage !== 'preview' || !result || !canvasRef.current) return;
    const cv = canvasRef.current;
    const { image, walls, rooms } = result;
    cv.width = image.width;
    cv.height = image.height;
    const ctx = cv.getContext('2d');
    const id = ctx.createImageData(image.width, image.height);
    id.data.set(image.data);
    ctx.putImageData(id, 0, 0);
    // هر فضا با رنگِ کمی متفاوت تا قسمت‌های جداشده دیده شوند
    const tints = ['rgba(10, 102, 194, 0.16)', 'rgba(31, 157, 85, 0.16)', 'rgba(214, 140, 20, 0.18)', 'rgba(140, 70, 200, 0.14)'];
    rooms.forEach((sp, si) =>
      (sp.rects || []).forEach((q) => {
        ctx.fillStyle = tints[si % tints.length];
        if (q.xl0 === undefined) {
          ctx.fillRect(q.x1, q.y1, q.x2 - q.x1, q.y2 - q.y1);
          return;
        }
        // تکه‌ی ذوزنقه‌ای (کنارِ دیوارِ مورب/منحنی)
        ctx.beginPath();
        ctx.moveTo(q.xl0, q.y1);
        ctx.lineTo(q.xr0, q.y1);
        ctx.lineTo(q.xr1, q.y2);
        ctx.lineTo(q.xl1, q.y2);
        ctx.closePath();
        ctx.fill();
      })
    );
    ctx.strokeStyle = '#e5484d';
    ctx.lineWidth = Math.max(3, image.width / 220);
    ctx.lineCap = 'round';
    walls.forEach((w) => {
      ctx.beginPath();
      if (isArc(w)) {
        const pts = arcPoints(w, 6);
        ctx.moveTo(pts[0].x, pts[0].y);
        pts.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
      } else {
        ctx.moveTo(w.x1, w.y1);
        ctx.lineTo(w.x2, w.y2);
      }
      ctx.stroke();
    });
    if (entrance) {
      // سنجاقِ سبز + برچسبِ «ورودی»
      const ms = result.minSide;
      const r = Math.max(9, ms * 0.022);
      const { x, y } = entrance;
      ctx.fillStyle = '#1f9d55';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = Math.max(2, r * 0.3);
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.arc(x, y - r * 1.9, r, Math.PI * 0.75, Math.PI * 2.25);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(x, y - r * 1.9, r * 0.38, 0, Math.PI * 2);
      ctx.fill();
      const fs = Math.max(14, ms * 0.032);
      ctx.font = `700 ${fs}px Vazirmatn, sans-serif`;
      const tw = ctx.measureText('ورودی').width + fs * 0.9;
      const lx = Math.min(image.width - tw / 2 - 2, Math.max(tw / 2 + 2, x));
      const ly = Math.max(fs, y - r * 3.6);
      ctx.fillStyle = '#1f9d55';
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(lx - tw / 2, ly - fs * 0.8, tw, fs * 1.6, fs * 0.8);
      else ctx.rect(lx - tw / 2, ly - fs * 0.8, tw, fs * 1.6);
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('ورودی', lx, ly + fs * 0.05);
    }
  }, [stage, result, entrance]);

  const areaNum = Number(toLatin(area)) || 0;
  const rooms = result ? result.rooms.length : 0;
  const walls = result ? result.walls.length : 0;

  return (
    <div className="sketch-import">
      <div className="sketch-import__sheet">
        <header className="sketch-import__head">
          <strong>پلان از روی کاغذ</strong>
          <button type="button" onClick={onClose} aria-label="بستن">
            <X size={20} strokeWidth={1.9} />
          </button>
        </header>

        {stage === 'intro' && (
          <div className="sketch-import__body">
            <div className="sketch-import__hero">
              <PenLine size={30} strokeWidth={1.6} />
            </div>
            <p className="sketch-import__lead">
              پلانِ خانه را روی یک کاغذِ سفید بکشید و عکسش را بگیرید — دیوارها خودشان ساخته می‌شوند.
            </p>
            <ul className="sketch-import__tips">
              <li>با <b>خودکار یا ماژیکِ تیره</b> بکشید (مدادِ کم‌رنگ سخت‌تر خوانده می‌شود).</li>
              <li>دیوارها را <b>بسته</b> بکشید — هر اتاق از چهار طرف به دیوار برسد. صاف بودنِ خط مهم نیست.</li>
              <li>اندازه‌ها لازم نیست دقیق باشند؛ فقط <b>نسبت‌ها</b> تقریباً درست باشد.</li>
              <li>
                جایی که درِ ورودی است، <b>بیرونِ خانه</b> بنویسید <b>«ورودی»</b> — بازدید از همان نقطه شروع می‌شود.
              </li>
              <li>اسمِ اتاق‌ها را داخلِ خودِ اتاق بنویسید (یا ننویسید). درها و پنجره‌ها را بعداً با ابزارِ خودشان می‌گذارید.</li>
              <li>موقعِ عکس، <b>کلِ کاغذ</b> در کادر باشد و کاغذ روی سطحی تیره‌تر یا رنگی باشد.</li>
            </ul>
            {hasPlan && <p className="sketch-import__warn">پلانی که الان کشیده‌اید با پلانِ کاغذ جایگزین می‌شود.</p>}
            <div className="sketch-import__actions">
              <button type="button" className="is-primary" onClick={() => cameraRef.current && cameraRef.current.click()}>
                <Camera size={18} strokeWidth={2} />
                <span>گرفتنِ عکس</span>
              </button>
              <button type="button" onClick={() => galleryRef.current && galleryRef.current.click()}>
                <ImageIcon size={18} strokeWidth={2} />
                <span>از گالری</span>
              </button>
            </div>
          </div>
        )}

        {stage === 'working' && (
          <div className="sketch-import__body is-center">
            <div className="sketch-import__spinner" />
            <p>در حالِ خواندنِ پلان…</p>
          </div>
        )}

        {stage === 'error' && (
          <div className="sketch-import__body is-center">
            <p>{error}</p>
            <div className="sketch-import__actions">
              <button type="button" className="is-primary" onClick={() => setStage('intro')}>
                <RotateCcw size={17} strokeWidth={2} />
                <span>دوباره</span>
              </button>
            </div>
          </div>
        )}

        {stage === 'preview' && result && (
          <div className="sketch-import__body">
            <div className="sketch-import__preview">
              <canvas ref={canvasRef} onClick={pickEntrance} />
            </div>
            <p className={`sketch-import__found ${rooms ? '' : 'is-bad'}`}>
              {rooms
                ? `${fa(walls)} دیوار و ${fa(rooms)} فضای بسته پیدا شد.`
                : walls
                  ? `${fa(walls)} دیوار پیدا شد ولی هیچ فضای بسته‌ای نه — احتمالاً دیوارها به هم نرسیده‌اند. می‌توانید همین را بردارید و در پلان کاملش کنید، یا با دیوارهای بسته دوباره عکس بگیرید.`
                  : 'خطی پیدا نشد. با خودکارِ پررنگ‌تر بکشید یا «خط‌ها کم‌رنگ است» را بزنید.'}
            </p>
            {walls > 0 && (
              <p className={`sketch-import__entry ${entrance ? '' : 'is-missing'}`}>
                {entrance
                  ? '«ورودی» پیدا شد (سنجاقِ سبز) — بازدید از همین نقطه شروع می‌شود. اگر جایش درست نیست، روی جای درست لمس کنید.'
                  : '«ورودی» پیدا نشد — روی جایی که بازدید باید از آن‌جا شروع شود لمس کنید (مثلاً بیرونِ درِ ورودی).'}
              </p>
            )}
            <label className="sketch-import__area">
              <span>متراژِ کلِ خانه</span>
              <input
                type="text"
                inputMode="decimal"
                value={area ? String(area).replace(/\d/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[d]) : ''}
                placeholder="مثلاً ۸۵"
                onChange={(e) => setArea(toLatin(e.target.value).slice(0, 6))}
              />
              <em>متر مربع</em>
            </label>
            <p className="sketch-import__note">
              اندازه‌ی واقعیِ پلان از همین متراژ حساب می‌شود
              {propertyArea ? ' (از آگهی)' : ''}. از عکس فقط دیوارها برداشته می‌شود؛ بعد هر دیواری را مثلِ همیشه می‌توانید
              جابه‌جا کنید.
            </p>
            <div className="sketch-import__actions">
              <button
                type="button"
                className="is-primary"
                disabled={!walls || areaNum <= 5}
                onClick={() =>
                  onApply({
                    walls: result.walls,
                    entrance: entrance ? { x: entrance.x, y: entrance.y } : null,
                    areaM2: areaNum,
                  })
                }
              >
                <Check size={18} strokeWidth={2.2} />
                <span>استفاده از این پلان</span>
              </button>
              <button type="button" onClick={() => setStage('intro')}>
                <RotateCcw size={17} strokeWidth={2} />
                <span>عکسِ دیگر</span>
              </button>
            </div>
            <button
              type="button"
              className={`sketch-import__sens ${sensitive ? 'is-on' : ''}`}
              onClick={() => {
                const next = !sensitive;
                setSensitive(next);
                run(lastFile, next);
              }}
            >
              {sensitive ? 'حساسیتِ معمولی' : 'خط‌ها کم‌رنگ است؟ حساس‌تر بخوان'}
            </button>
          </div>
        )}

        <input
          ref={cameraRef}
          type="file"
          accept="image/*"
          capture="environment"
          hidden
          onChange={(e) => {
            run(e.target.files && e.target.files[0], sensitive);
            e.target.value = '';
          }}
        />
        <input
          ref={galleryRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            run(e.target.files && e.target.files[0], sensitive);
            e.target.value = '';
          }}
        />
      </div>
    </div>
  );
}

export default SketchImport;
